import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { Button } from '@/shared/ui';
import type { Role } from '../../model/types';

export interface BulkChanges {
  roleId?: string;
  isActive?: boolean;
}

interface Props {
  selectedCount: number;
  roles: Role[];
  isSaving?: boolean;
  onSave: (changes: BulkChanges) => void;
  onCancel: () => void;
}

export function BulkActionsBar({
  selectedCount,
  roles,
  isSaving = false,
  onSave,
  onCancel,
}: Props) {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<'' | 'true' | 'false'>(
    '',
  );

  const hasChanges = selectedRole !== null || selectedStatus !== '';

  const handleSave = () => {
    const changes: BulkChanges = {};
    if (selectedRole) changes.roleId = selectedRole.id;
    if (selectedStatus !== '') changes.isActive = selectedStatus === 'true';
    onSave(changes);
    setSelectedRole(null);
    setSelectedStatus('');
  };

  const handleCancel = () => {
    setSelectedRole(null);
    setSelectedStatus('');
    onCancel();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.875,
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
        borderRadius: 2,
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: 'info.dark',
          mr: 0.5,
          flexShrink: 0,
        }}
      >
        {selectedCount} selected
      </Typography>

      <Autocomplete
        size="small"
        options={roles}
        value={selectedRole}
        onChange={(_, value) => setSelectedRole(value)}
        getOptionLabel={(option) => option.name ?? '—'}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Set role…"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                fontSize: '0.8125rem',
              },
            }}
          />
        )}
        sx={{ width: 200 }}
      />

      <Select
        size="small"
        value={selectedStatus}
        displayEmpty
        renderValue={(value) => {
          if (!value)
            return (
              <Box component="span" sx={{ color: 'text.disabled' }}>
                Set status…
              </Box>
            );
          return value === 'true' ? 'Active' : 'Deactivated';
        }}
        sx={{
          fontSize: '0.8125rem',
          minWidth: 140,
          borderRadius: 1.5,
          '& .MuiSelect-select': {
            paddingTop: '6.5px',
            paddingBottom: '6.5px',
          },
        }}
        MenuProps={{
          slotProps: {
            paper: {
              sx: { borderRadius: 2 },
            },
          },
          MenuListProps: {
            sx: { p: 0, fontSize: '0.875rem' },
          },
        }}
        onChange={(e) =>
          setSelectedStatus(e.target.value as '' | 'true' | 'false')
        }
      >
        <MenuItem value="true" sx={{ py: 0.75, fontSize: '0.875rem' }}>
          Active
        </MenuItem>
        <MenuItem value="false" sx={{ py: 0.75, fontSize: '0.875rem' }}>
          Deactivated
        </MenuItem>
      </Select>

      <Box sx={{ flexGrow: 1 }} />

      <Button
        disabled={!hasChanges || isSaving}
        sx={{ py: 0.375, px: 1.125, fontSize: '0.6875rem', minWidth: 'unset' }}
        onClick={handleSave}
      >
        Save
      </Button>
      <Button
        variant="outlined"
        color="secondary"
        sx={{ py: 0.375, px: 1.125, fontSize: '0.6875rem', minWidth: 'unset' }}
        onClick={handleCancel}
      >
        Cancel
      </Button>
    </Box>
  );
}
